import {
  clearSession,
  customerSessionSchema,
  IPublicSession,
  isSessionOrganizer,
  resetSession,
  sharedSessionSchema,
  updatePublicSession,
  updatePublicSessionNow,
  userSession,
} from 'state/session';
import { user } from 'utils/auth/user';
import { State } from 'utils/state';
import { IntegrationType, integrationType } from 'utils/ui/app';
import { markDirty, resetDirtyKeys } from './dirtyKeys';
import { cancelDirtyFlush } from './dirtyFlush';
import { isApplyingRemote } from './remoteApplyGuard';
import { emptyLiveStreamIdentifiersToOmit } from './liveStreamClobberGuard';
import { READINESS_PSEUDO_KEYS } from './computed';

/** Trailing meta argument of State change emissions (see State.setValues JSDoc). */
type TChangeMeta = { reset?: boolean } | undefined;

/**
 * Sink for the Session Flight Recorder's `sync` lane (AVS-4429, parent AVS-4423).
 *
 * This listener has ten-odd guard branches in front of its write, and a field that leaves through
 * one of them never reaches the server *and leaves no trace*. That is the AVS-4411 shape: on
 * 2026-09-04 an empty `aveSessionId` on the server could only be proved by hand-fetching the record
 * from inside a customer page, hours later. Reporting the branch turns it into one line in a
 * timeline.
 *
 * The sink lives here rather than the listener importing `telemetry/recorder` because the build
 * gate (`scripts/recorder-build-gate.mjs`) stubs out exactly one module — the recorder's ENTRY. An
 * import from app code into `telemetry/recorder/core` would drag the recorder into the prod
 * artefact, which acceptance criterion 4 of AVS-4423 forbids. Same discipline as
 * `setListenerGuardReporter` (`utils/events.ts:61`): a module-level settable function defaulting to
 * a no-op, called from inside the owner, isolated in its own try/catch.
 *
 * A SINK, deliberately not a `change` listener. A listener returning a falsy value participates in
 * the prevent-execution protocol (`utils/events.ts:288`), and one more `userSession()` listener
 * would also shift the very counters `syncTelemetryProbe.readSyncTelemetry` reports — corrupting
 * the leak metric AVS-4332 exists to watch.
 *
 * @param decision - the branch this change left through, e.g. `skip:offline`, `write:debounced`
 * @param detail - the branch's own evidence; `keys` is passed LIVE and must not be retained
 */
export type TSyncDecisionSink = (decision: string, detail: Record<string, unknown>) => void;

function noopSyncDecisionSink(): void {
  // Default sink: nothing is recording, and each decision pays one no-op call.
}

let syncDecisionSink: TSyncDecisionSink = noopSyncDecisionSink;

/** Register the recorder's sync lane. Pass `null` to restore the no-op. */
export function setSyncDecisionSink(sink: TSyncDecisionSink | null): void {
  syncDecisionSink = sink ?? noopSyncDecisionSink;
}

/**
 * The sink is registered from outside and arbitrary — a throwing one must never break a session
 * write. Only the call itself is inside the try; `console.error` rather than `logger` because
 * several suites hand-mock `utils/log` with a partial object.
 */
function reportSyncDecision(decision: string, detail: Record<string, unknown>): void {
  try {
    syncDecisionSink(decision, detail);
  } catch (error) {
    console.error('[Session] sync-decision sink threw and was isolated', error);
  }
}

export function syncToServer(state: State<IPublicSession>) {
  state.on(
    'change',
    (
      keys: string[],
      arrValues: unknown[],
      arrOldValues: unknown[],
      _sessionChanges: Partial<IPublicSession>,
      _currentSession: IPublicSession,
      meta?: TChangeMeta
    ) => {
      // A reset emission announces reverts, not local edits: wipe the dirty set and kill any
      // pending debounced PUT so a stale payload can never resurrect the torn-down session (AVS-4331)
      if (meta?.reset) {
        resetDirtyKeys();
        updatePublicSession.cancel();
        // A scheduled re-send would outlive the session it was queued for and write to — or
        // resurrect — a record that no longer exists (AVS-4399, the AVS-4331 failure shape).
        cancelDirtyFlush();
        reportSyncDecision('skip:reset', { keys, role: user.type });
        return;
      }

      // A flags-only emission (readiness pseudo-keys) drives UI reactivity only — never dirty,
      // never PUT (AVS-4331 T6)
      if (keys.every((key) => READINESS_PSEUDO_KEYS.has(key))) {
        reportSyncDecision('skip:readiness-keys', { keys, role: user.type });
        return;
      }

      // For local AVE sessions, we rely on the syncing prStrings via the broadcast, as we cannot leverage 3D Showroom prString change events as it is not rendered in all tabs (local AVEs only support on tab/stream at a time)
      const updateEverything =
        integrationType() === IntegrationType.WebComponent &&
        (userSession().get('activeExperience') === '2D' || userSession().isLocalAveSession);
      // Block syncing when offline (except for offline flag changes)
      if (userSession().get('offline') && !keys.includes('offline')) {
        console.warn('Session sync blocked: offline mode is enabled');
        reportSyncDecision('skip:offline', { keys, role: user.type });
        return;
      }

      // do not proceed if session is starting or ending
      if (keys.includes('id') || keys.includes('endedAt')) {
        if (keys.includes('endedAt') && integrationType() === IntegrationType.WebComponent) {
          // Tear down only when endedAt BECAME truthy — a revert announcement or a remote echo
          // carrying a different truthy timestamp must not re-trigger the reset (AVS-4331)
          const endedIdx = keys.indexOf('endedAt');
          if (!arrOldValues[endedIdx] && !!arrValues[endedIdx]) {
            // Reset ended session for web component case (handled outside of wc context in standalone)
            clearSession();
            resetSession();
          }
        }
        // AVS-4080: When endedAt arrives in state (e.g. loadPublicSession resolved with a
        // soft-deleted scheduled session), cancel any pending debounced PUT. Otherwise a
        // pre-load brand/market write queued by contextLoader will fire ~1s later with the
        // ended payload, the lambda rejects with 404 SESSION_NOT_FOUND, and sendApiRequest
        // renders a duplicate red toast on top of the page's own error UI.
        if (keys.includes('endedAt')) {
          updatePublicSession.cancel();
          cancelDirtyFlush();
        }
        reportSyncDecision('skip:lifecycle-key', {
          keys,
          role: user.type,
          idChanged: keys.includes('id'),
          endedAtChanged: keys.includes('endedAt'),
        });
        return;
      }

      // AVS-4080: Skip syncing once the session is ended. Loading a soft-deleted
      // scheduled session populates state with endedAt; downstream listeners (e.g.
      // initCarSelectionMethod) then mutate other fields and would otherwise trigger
      // a PUT that includes endedAt in the body. Cancel any pending debounce here too
      // in case it slipped past the change-with-endedAt branch above.
      if (userSession().get('endedAt')) {
        updatePublicSession.cancel();
        cancelDirtyFlush();
        reportSyncDecision('skip:session-ended', { keys, role: user.type });
        return;
      }

      // Customer should never sync aveSessionId to server (dealer-only field)
      // This prevents race conditions where customer's initial load overwrites dealer's value
      if (!user.isDealer && (keys.includes('aveSessionId') || keys.includes('aveChannelName'))) {
        console.warn('Customer cannot sync aveSessionId or aveChannelName to server. Skipping update.');
        reportSyncDecision('skip:customer-restricted-field', { keys, role: user.type });
        return;
      }

      // Only the session organizer can change activeExperience (AVS-4141)
      // Non-organizer dealers must follow the organizer's experience choice
      if (user.isDealer && keys.includes('activeExperience') && !isSessionOrganizer()) {
        console.warn('Non-organizer cannot sync activeExperience to server. Skipping update.');
        reportSyncDecision('skip:not-organizer', { keys, role: user.type });
        return;
      }

      if (userSession().get('isShared') && (keys.includes('brand') || keys.includes('market'))) {
        console.warn('Changes to brand or market while sharing are not allowed. Skipping session update.');
        reportSyncDecision('skip:shared-brand-market', { keys, role: user.type });
        return;
      }

      // A remote apply (broadcast / reconnect read-repair) must not be treated as a local edit: it
      // must neither mark keys dirty nor echo a write back to the server (AVS-4330). The lifecycle
      // and permission guards above still run for remote-origin changes (e.g. an `endedAt` broadcast
      // still tears the session down) — only the sync side-effects below are suppressed.
      if (isApplyingRemote()) {
        reportSyncDecision('skip:applying-remote', { keys, role: user.type });
        return;
      }

      const sessionValues = userSession().get();

      let keysToSync = [...keys];
      // Keys the guard suppresses. Sent as `omitKeys` rather than a frozen `data` snapshot so the
      // body is rebuilt from live state when the debounce fires (AVS-4399): a snapshot taken here
      // would carry pre-broadcast values back to the server if a remote payload lands inside the
      // 1 s window, and CAS cannot reject it because `clientVersion` is read at fire time.
      let omitKeys: (keyof IPublicSession)[] | undefined;

      // Shared with the re-send driver, which rebuilds its body from live state and would otherwise
      // bypass this entirely (AVS-4399).
      const guardedKeys = emptyLiveStreamIdentifiersToOmit(keys as (keyof IPublicSession)[]);

      if (guardedKeys.length > 0) {
        keysToSync = keysToSync.filter((key) => !guardedKeys.includes(key as keyof IPublicSession));
        omitKeys = guardedKeys;
        console.warn('Clobber guard fired: dropped empty live-stream identifier(s) from outgoing sync', guardedKeys);
        // Not a skip — a PARTIAL drop. `aveSessionId` leaving through here is the exact AVS-4411
        // shape, so it is recorded even though the rest of the payload still goes out.
        reportSyncDecision('guard:clobber-dropped', { keys, role: user.type, guardedKeys });
      } else if (
        user.isDealer &&
        ((keys.includes('aveSessionId') && sessionValues.aveSessionId === '') ||
          (keys.includes('aveChannelName') && sessionValues.aveChannelName === ''))
      ) {
        console.info(
          'Clobber guard evaluated: empty live-stream identifier allowed to sync (session ending, not streaming, or not in 3D)',
          {
            streamActive: sessionValues.streamActive,
            endedAt: sessionValues.endedAt,
            activeExperience: sessionValues.activeExperience,
          }
        );
        reportSyncDecision('guard:clobber-allowed', {
          keys,
          role: user.type,
          streamActive: sessionValues.streamActive,
          endedAt: sessionValues.endedAt,
          activeExperience: sessionValues.activeExperience,
        });
      }

      // only keys that are part of the internal session schema should trigger an update on database level
      // if none of those keys changed, we can skip the database update and just emit the event to the participants
      const relevantKeys = Object.keys(sharedSessionSchema.shape);

      // Mark locally-edited synced keys unconfirmed until the server acknowledges them, so an
      // incoming payload cannot overwrite an edit that is still in flight (AVS-4330). Scoped to
      // the same per-user-type schema updatePublicSession uses to build the outgoing body
      // (customerSessionSchema omits vehicle-registry fields, AVS-4400) — a key a customer's PUT
      // can never carry must not be marked dirty, or applyRemote/readRepair refuse the dealer's
      // value for that key for as long as it stays dirty (PR #1785 review).
      const dirtyableKeys = Object.keys(
        user.type === 'customer' ? customerSessionSchema.shape : sharedSessionSchema.shape
      );
      markDirty((keysToSync as (keyof IPublicSession)[]).filter((key) => dirtyableKeys.includes(String(key))));

      const syncOptions = omitKeys ? { updateEverything, omitKeys } : { updateEverything };

      if (!relevantKeys.some((key) => keysToSync.includes(key))) {
        if (keysToSync.length === 0) {
          reportSyncDecision('skip:no-keys-left', { keys, role: user.type, guardedKeys });
          return;
        }
        console.warn('No relevant keys changed for session update: ', keysToSync);
        reportSyncDecision('write:emit-only', { keys, role: user.type, keysToSync, updateEverything });
        updatePublicSession({ emitOnly: true, ...syncOptions });
        return;
      }

      // aveSessionId is critical for multi-dealer sync — bypass the debounce and send
      // immediately. This ensures a joining dealer reads the correct aveSessionId from
      // the server before creating its own AVE session. (AVS-4141)
      // Note: activeExperience intentionally uses the debounce to avoid a last-writer-wins
      // race between two dealers' immediate PUTs.
      if (keysToSync.includes('aveSessionId')) {
        reportSyncDecision('write:immediate', {
          keys,
          role: user.type,
          keysToSync,
          updateEverything,
          omitKeys,
          aveSessionId: sessionValues.aveSessionId,
        });
        updatePublicSession.cancel();
        updatePublicSessionNow(syncOptions);
        return;
      }

      reportSyncDecision('write:debounced', { keys, role: user.type, keysToSync, updateEverything, omitKeys });
      // send session data to the server (debounced)
      updatePublicSession(syncOptions);
    }
  );
}
